import moment from 'moment';
import { useCallback, useContext, useMemo } from 'react';
import { useSubscriptionContext } from '../SubscriptionProvider/Subscription.context';
import {
  ePlanCode,
  eSubscriptionFeatureCode,
  eSubscriptionStatus,
} from '../SubscriptionProvider/Subscription.enums';
import { useUserContext } from '../UserProvider/User.context';
import { eUserRole } from '../UserProvider/User.enums';
import { useWorkspaceContext } from '../WorkspaceProvider/Workspace.context';
import { ACLContext } from './ACL.context';
import { findNumericId } from './ACL.functions';
import { ACLContextType } from './ACL.types';
import {
  VITE_TIKTOK_ALLOWED_WORKSPACES,
  VITE_TIKTOK_FEATURE,
} from '../../constants/envs';
import { isOutboundMessagingBlocked } from '../../functions/planPause.functions';

export function useACLContext(): ACLContextType {
  return useContext(ACLContext);
}

export function useACL(): ACLContextType {
  const { currentUserRole } = useUserContext();
  const { subscription, isLoadingSubscription } = useSubscriptionContext();
  const {
    canSeeWapiIntegration,
    workspaceInfo,
    currentWorkspaceId,
    isRoleResolved,
  } = useWorkspaceContext();

  // AGI-320: fail closed. Until this workspace's own user list confirms the
  // role, every flag (and every capability built on them below) reads the
  // SELLER default the app boots with, whatever `currentUserRole` holds: a
  // role a fresh list no longer confirms, or the previous workspace's.
  const role = isRoleResolved ? currentUserRole : eUserRole.SELLER;
  const userIsOwner = role === eUserRole.OWNER;
  const userIsAdmin = role === eUserRole.ADMIN;
  const userIsSeller = role === eUserRole.SELLER;
  const userIsViewer = role === eUserRole.VIEWER;

  const isLegacyPlan = subscription.planCode === ePlanCode.PLAN_LEGACY;
  const isBusinessPlan = subscription.planCode === ePlanCode.PLAN_BASIC;
  const isGrowthPlan = subscription.planCode === ePlanCode.PLAN_PROFESSIONAL;
  const isAdvancedPlan = subscription.planCode === ePlanCode.PLAN_ENTERPRISE;

  const isTrial = subscription.status === eSubscriptionStatus.TRIAL;
  // Keep plan entitlements above keyed to the restored plan. A pause only
  // changes this capability; it must not masquerade as another plan. The
  // composer reads the same predicate, so no send surface can disagree with it.
  const canSendOutboundMessages = !isOutboundMessagingBlocked(subscription);

  const activeLeadsLimitFeature = subscription.features?.find(
    (feature) => feature.code === eSubscriptionFeatureCode.ACTIVE_LEADS_LIMIT,
  );

  const maxFunnelFeature = subscription?.features?.find(
    (feature) =>
      feature.code === eSubscriptionFeatureCode.FUNNELS_CREATION_LIMIT,
  );

  const storageLimitFeature = subscription.features?.find(
    (feature) => feature.code === eSubscriptionFeatureCode.STORAGE_CAPACITY_GB,
  );

  const leadbotLevelsAccessFeature = subscription?.features?.find(
    (feature) =>
      feature.code === eSubscriptionFeatureCode.LEADBOT_LEVELS_ACCESS,
  );

  const watermarkRemovalFeature = subscription?.features?.find(
    (feature) =>
      feature.code === eSubscriptionFeatureCode.LEADBOT_WATERMARK_REMOVAL,
  );

  const watermarkRemovalFeatureLegacy = subscription?.features?.find(
    (feature) => feature.code === eSubscriptionFeatureCode.CHAT_BOT,
  );

  const marketingMessageVolumeFeature = subscription?.features?.find(
    (feature) =>
      feature.code === eSubscriptionFeatureCode.MARKETING_MESSAGE_VOLUME_LIMIT,
  );

  const whatsAppApiAccessFeature = subscription?.features?.find(
    (feature) => feature.code === eSubscriptionFeatureCode.WHATSAPP_API_ACCESS,
  );

  const interactiveCustomFooterFeature = subscription?.features?.find(
    (feature) =>
      feature.code ===
      eSubscriptionFeatureCode.WHATSAPP_API_INTERACTIVE_CUSTOM_FOOTER,
  );

  const apiKeyAccessFeature = subscription?.features?.find(
    (feature) => feature.code === eSubscriptionFeatureCode.API_KEY,
  );

  const templateCreditsLimitFeature = subscription?.features?.find(
    (feature) =>
      feature.code === eSubscriptionFeatureCode.TEMPLATE_CREDITS_LIMIT,
  );

  const liveSupportAccessFeature = subscription?.features?.find(
    (feature) => feature.code === eSubscriptionFeatureCode.LIVE_SUPPORT_ACCESS,
  );

  const whatsAppApiAnalyticsAccessFeature = subscription?.features?.find(
    (feature) =>
      feature.code ===
      eSubscriptionFeatureCode.WHATSAPP_CAMPAIGN_ANALYTICS_ACCESS,
  );

  const activeLeadsLimit = activeLeadsLimitFeature
    ? (activeLeadsLimitFeature.value as number)
    : 0;
  const activeLeadsCount = activeLeadsLimitFeature?.usedCount ?? 0;
  const leadLimitAddonId = findNumericId(activeLeadsLimitFeature) ?? null;

  const maxFunnelCount = maxFunnelFeature
    ? (maxFunnelFeature.value as number)
    : 0;

  const storageLimit = storageLimitFeature
    ? (storageLimitFeature.value as number)
    : 30;

  const storageCount = storageLimitFeature?.usedCount ?? 0;

  const templateCreditsLimitForTrial =
    templateCreditsLimitFeature?.items?.find((item) => {
      if (!item.startDate || !item.endDate) return false;

      const startDate = moment(item.startDate);
      const endDate = moment(item.endDate);
      const daysDifference = endDate.diff(startDate, 'days');

      return daysDifference === 14;
    })?.limit ?? 0;
  const templateCreditsLimit =
    (isTrial && templateCreditsLimitFeature && templateCreditsLimitForTrial) ||
    (templateCreditsLimitFeature &&
      (templateCreditsLimitFeature.value as number)) ||
    0;

  // RET-1540 — recurring plan grant (base + recurring add-ons), excluding
  // one-time top-up packs. Display surfaces (Subscription panel) show this so
  // a one-time credit purchase is not presented as part of the plan. Falls
  // back to the aggregate `templateCreditsLimit` when the BFF omits
  // `recurringValue` (rollout window). `templateCreditsLimit` (aggregate) is
  // kept untouched — the WAPI upgrade/downgrade math depends on it.
  const templateCreditsPlanLimit =
    typeof templateCreditsLimitFeature?.recurringValue === 'number'
      ? templateCreditsLimitFeature.recurringValue
      : templateCreditsLimit;

  // RET-1487 Sub-3 — numeric FEATURE id for TEMPLATE_CREDITS_LIMIT, used by the
  // recurring-credits cancel flow to match the right `workspace_feature_overrides`
  // row from `GET /tiers/overrides` (the override id is NOT derivable from
  // `findNumericId(items)` for this feature — leadbilling only injects the
  // override id into `items[]` for LEAD_LIMIT, so template_credits item ids are
  // addon-match ids, not the override id the cancel endpoint needs). See
  // reference_ret1487_recurring_override_id_via_tiers_overrides.
  const templateCreditsLimitFeatureId = templateCreditsLimitFeature?.id ?? null;

  const leadbotLevelsAccess = !!leadbotLevelsAccessFeature?.value;

  const liveSupportAccess = !!liveSupportAccessFeature?.value;

  const whatsAppApiAnalyticsAccess = !!whatsAppApiAnalyticsAccessFeature?.value;

  const watermarkRemoval =
    !!watermarkRemovalFeature?.value || !!watermarkRemovalFeatureLegacy?.value;

  const watermarkRemovalId =
    findNumericId(watermarkRemovalFeature) ??
    findNumericId(watermarkRemovalFeatureLegacy) ??
    null;

  const leadbotLevelsAccessId =
    findNumericId(leadbotLevelsAccessFeature) ?? null;

  const marketingMessageVolume = marketingMessageVolumeFeature?.value as number;

  const whatsAppApiAccess = !!whatsAppApiAccessFeature?.value;

  // Gates the edit-footer affordance on the WAPI interactive composer.
  // Trial users get full editability even without the entitlement row
  // (mirrors canCustomizeAgentName — we want trial accounts to experience
  // the paid UX so the downgrade to Pro at conversion feels like a real
  // loss). Advanced users carry the billing entitlement directly; Pro and
  // Basic see a locked "via leadsales" footer as an upsell lever.
  const canEditInteractiveFooter =
    isTrial || !!interactiveCustomFooterFeature?.value;

  const apiKeyAccess = !!apiKeyAccessFeature?.value;

  const canUserCreateNewFunnel = useCallback(
    (currentFunnelCount?: number) => {
      if (!workspaceInfo?.isActive) {
        return false;
      }

      const userCanCreateFunnel = userIsOwner || userIsAdmin;

      if (isLegacyPlan) {
        // Unlimited funnels
        return userCanCreateFunnel;
      }

      if (isAdvancedPlan) {
        // Unlimited funnels
        return userCanCreateFunnel;
      }

      if (!currentFunnelCount) {
        return false;
      }

      if (isGrowthPlan) {
        // Limited funnels
        return (
          userCanCreateFunnel &&
          (typeof maxFunnelCount === 'number' ||
            typeof maxFunnelCount === 'string') &&
          currentFunnelCount < Number(maxFunnelCount)
        );
      }

      if (isBusinessPlan) {
        // Limited funnels
        return (
          userCanCreateFunnel &&
          (typeof maxFunnelCount === 'number' ||
            typeof maxFunnelCount === 'string') &&
          currentFunnelCount < Number(maxFunnelCount)
        );
      }

      return false;
    },
    [
      isAdvancedPlan,
      isBusinessPlan,
      isGrowthPlan,
      isLegacyPlan,
      maxFunnelCount,
      userIsAdmin,
      userIsOwner,
      workspaceInfo?.isActive,
    ],
  );

  const canUserEditFunnels = useCallback(() => {
    const userCanEditFunnels = userIsOwner || userIsAdmin;

    if (isLegacyPlan) {
      return userCanEditFunnels;
    }

    if (isAdvancedPlan) {
      return userCanEditFunnels;
    }

    if (isGrowthPlan) {
      return userCanEditFunnels;
    }

    if (isBusinessPlan) {
      return userCanEditFunnels;
    }

    return false;
  }, [
    isAdvancedPlan,
    isBusinessPlan,
    isGrowthPlan,
    isLegacyPlan,
    userIsAdmin,
    userIsOwner,
  ]);

  const canUserOpenAutoAssignModal = useCallback(() => {
    const userCanEditFunnels = userIsOwner || userIsAdmin;

    if (isLegacyPlan) {
      return userCanEditFunnels;
    }

    if (isAdvancedPlan) {
      return userCanEditFunnels;
    }

    if (isGrowthPlan) {
      return userCanEditFunnels;
    }

    if (isBusinessPlan) {
      return userCanEditFunnels;
    }

    return false;
  }, [
    isAdvancedPlan,
    isBusinessPlan,
    isGrowthPlan,
    isLegacyPlan,
    userIsAdmin,
    userIsOwner,
  ]);

  const canLeadsBeCreatedOrActivated = useCallback(() => {
    if (isLegacyPlan) {
      // Unlimited leads
      return true;
    }

    if (!activeLeadsLimitFeature) {
      return false;
    }

    if (isAdvancedPlan) {
      // Limited leads
      return activeLeadsCount <= Number(activeLeadsLimit);
    }

    if (isGrowthPlan) {
      // Limited leads
      return activeLeadsCount <= Number(activeLeadsLimit);
    }

    if (isBusinessPlan) {
      // Limited leads
      return activeLeadsCount <= Number(activeLeadsLimit);
    }

    return false;
  }, [
    activeLeadsCount,
    activeLeadsLimit,
    activeLeadsLimitFeature,
    isAdvancedPlan,
    isBusinessPlan,
    isGrowthPlan,
    isLegacyPlan,
  ]);

  const canUserUploadToStorage = useCallback(() => {
    return storageCount <= storageLimit;
  }, [storageCount, storageLimit]);

  const canUserChatWithHumanSupport = useCallback(() => {
    if (isLegacyPlan) {
      return true;
    }

    if (isAdvancedPlan) {
      return true;
    }

    if (isGrowthPlan) {
      return true;
    }

    if (isBusinessPlan) {
      return false;
    }

    return false;
  }, [isAdvancedPlan, isBusinessPlan, isGrowthPlan, isLegacyPlan]);

  const canUserSendMessagesInBulkInFunnel = useCallback(() => {
    if (isLegacyPlan) {
      return false;
    }

    if (isAdvancedPlan) {
      return true;
    }

    if (isGrowthPlan) {
      return true;
    }

    if (isBusinessPlan) {
      return false;
    }

    return false;
  }, [isAdvancedPlan, isBusinessPlan, isGrowthPlan, isLegacyPlan]);

  const canWorkspaceHaveFreeTrial = useCallback(() => {
    if (isLegacyPlan) {
      return false;
    }

    if (isAdvancedPlan) {
      return true;
    }

    if (isGrowthPlan) {
      return true;
    }

    if (isBusinessPlan) {
      return false;
    }

    return true; // The default case must be true to avoid having workspaces without a paid free trial
  }, [isAdvancedPlan, isBusinessPlan, isGrowthPlan, isLegacyPlan]);

  const canUserAccessLeadbotLevels = useCallback(() => {
    if (isLegacyPlan) {
      return false;
    }

    if (isAdvancedPlan) {
      return true;
    }

    if (isGrowthPlan) {
      return true;
    }

    if (isBusinessPlan) {
      return leadbotLevelsAccess;
    }

    return false;
  }, [
    isAdvancedPlan,
    isBusinessPlan,
    isGrowthPlan,
    isLegacyPlan,
    leadbotLevelsAccess,
  ]);

  const canUserAccessWhatsAppApi = useCallback(() => {
    if (canSeeWapiIntegration) {
      return true;
    }

    if (isGrowthPlan || isAdvancedPlan) {
      return whatsAppApiAccess;
    }

    if (isLegacyPlan || isBusinessPlan) {
      return false;
    }

    return false;
  }, [
    canSeeWapiIntegration,
    isAdvancedPlan,
    isBusinessPlan,
    isGrowthPlan,
    isLegacyPlan,
    whatsAppApiAccess,
  ]);

  const canUserAccessTikTok = useCallback(() => {
    if (!VITE_TIKTOK_FEATURE) return false;
    if (
      VITE_TIKTOK_ALLOWED_WORKSPACES.length > 0 &&
      (!currentWorkspaceId ||
        !VITE_TIKTOK_ALLOWED_WORKSPACES.includes(currentWorkspaceId))
    ) {
      return false;
    }
    return isGrowthPlan || isAdvancedPlan;
  }, [currentWorkspaceId, isAdvancedPlan, isGrowthPlan]);

  const canUserSeeComingSoonTikTok = useCallback(() => {
    // Flag OFF (prod today): Growth/Advanced still see "Próximamente".
    // Flag ON + workspace not in allowlist: also "Próximamente".
    // Only workspaces with actual access (or non-eligible plans) see no badge.
    return !canUserAccessTikTok() && (isGrowthPlan || isAdvancedPlan);
  }, [canUserAccessTikTok, isAdvancedPlan, isGrowthPlan]);

  const canUserAccessWhatsAppWebQr = useCallback(() => {
    if (
      isLegacyPlan ||
      isBusinessPlan ||
      (!workspaceInfo?.isActive && !isLoadingSubscription)
    ) {
      return true;
    }
    return false;
  }, [
    isBusinessPlan,
    isLegacyPlan,
    isLoadingSubscription,
    workspaceInfo?.isActive,
  ]);

  return useMemo(
    () => ({
      isTrial,
      isRoleResolved,
      userIsOwner,
      userIsAdmin,
      userIsSeller,
      userIsViewer,
      isLegacyPlan,
      isBusinessPlan,
      isGrowthPlan,
      isAdvancedPlan,
      activeLeadsLimit,
      activeLeadsCount,
      maxFunnelCount,
      storageLimit,
      watermarkRemoval,
      watermarkRemovalId,
      leadbotLevelsAccessId,
      marketingMessageVolume,
      apiKeyAccess,
      templateCreditsLimit,
      templateCreditsPlanLimit,
      templateCreditsLimitFeatureId,
      whatsAppApiAccess,
      canEditInteractiveFooter,
      leadbotLevelsAccess,
      leadLimitAddonId,
      liveSupportAccess,
      whatsAppApiAnalyticsAccess,
      leadbotLevelsAccessFeature,
      liveSupportAccessFeature,
      watermarkRemovalFeature,
      canUserCreateNewFunnel,
      canUserEditFunnels,
      canUserOpenAutoAssignModal,
      canLeadsBeCreatedOrActivated,
      canUserUploadToStorage,
      canUserChatWithHumanSupport,
      canUserSendMessagesInBulkInFunnel,
      canWorkspaceHaveFreeTrial,
      canUserAccessLeadbotLevels,
      canUserAccessWhatsAppApi,
      canUserAccessWhatsAppWebQr,
      canUserAccessTikTok,
      canUserSeeComingSoonTikTok,
      canSendOutboundMessages,
    }),
    [
      activeLeadsCount,
      activeLeadsLimit,
      apiKeyAccess,
      canEditInteractiveFooter,
      canLeadsBeCreatedOrActivated,
      canSendOutboundMessages,
      canUserAccessLeadbotLevels,
      canUserAccessTikTok,
      canUserAccessWhatsAppApi,
      canUserAccessWhatsAppWebQr,
      canUserChatWithHumanSupport,
      canUserCreateNewFunnel,
      canUserEditFunnels,
      canUserOpenAutoAssignModal,
      canUserSeeComingSoonTikTok,
      canUserSendMessagesInBulkInFunnel,
      canUserUploadToStorage,
      canWorkspaceHaveFreeTrial,
      isAdvancedPlan,
      isBusinessPlan,
      isGrowthPlan,
      isLegacyPlan,
      isRoleResolved,
      isTrial,
      leadLimitAddonId,
      leadbotLevelsAccess,
      leadbotLevelsAccessFeature,
      leadbotLevelsAccessId,
      liveSupportAccess,
      liveSupportAccessFeature,
      marketingMessageVolume,
      maxFunnelCount,
      storageLimit,
      templateCreditsLimit,
      templateCreditsLimitFeatureId,
      templateCreditsPlanLimit,
      userIsAdmin,
      userIsOwner,
      userIsSeller,
      userIsViewer,
      watermarkRemoval,
      watermarkRemovalFeature,
      watermarkRemovalId,
      whatsAppApiAccess,
      whatsAppApiAnalyticsAccess,
    ],
  );
}
